import { useEffect, useMemo, useState } from "react";
import { newAxiosClient } from "@shadowcue/ui";
import type { IPagination } from "@shadowcue/ui";

export interface IScriptTemplateSummary extends Record<string, unknown> {
  id: number;
  name: string;
  createdAt: string;
  scriptTemplateSectionsCount: number;
  scriptTemplateQuestionsCount: number;
}

interface UseScriptTemplatesResult {
  templates: IScriptTemplateSummary[];
  totalPages: number;
  loading: boolean;
  error: boolean;
}

/**
 * Loads a page of the newest script templates for the dashboard's table. The
 * API scopes them to the viewer's organization, so members see the same list
 * as admins.
 */
const useScriptTemplates = (
  authToken: string,
  limit: number,
  page: number,
): UseScriptTemplatesResult => {
  const axios = useMemo(() => newAxiosClient(), []);

  const [templates, setTemplates] = useState<IScriptTemplateSummary[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get<{
          scriptTemplates: IScriptTemplateSummary[];
          pagination: IPagination;
        }>("/api/script_templates", {
          headers: { AUTHORIZATION: `Bearer ${authToken}` },
          params: { includeCounts: true, sort: "newest", page, limit },
        });
        setTemplates(res.data.scriptTemplates || []);
        setTotalPages(res.data.pagination?.pages || 1);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [authToken, limit, page]);

  return { templates, totalPages, loading, error };
};

export default useScriptTemplates;
